/**
 * Loads theatre photos from src/images/Theatre_images/{THEATRE_NAME}/*
 * (see https://www.bingenbash.com/booknow/miyapur style cards with gallery).
 * Dynamic URLs from API are merged in (see fetchTheatreGalleryByNames).
 */

import {
  THEATRE_GALLERY_BY_NAMES,
  THEATRE_GALLERY_VIDEOS_BY_NAMES,
  THEATRE_EXPERIENCE_HIGHLIGHTS_BY_NAMES,
  HOST,
} from "../api/constants";
import { resolveTheaterImageSrc } from "./theaterHeroSlides";

/** Full URL for img when path is relative (SPA on :3000, API on :8080). */
export function absoluteMediaUrl(path) {
  if (path == null || path === "") return "";
  const s = String(path);
  if (/^https?:\/\//i.test(s)) return s;
  const base = (HOST || "").replace(/\/$/, "");
  return base + (s.startsWith("/") ? s : `/${s}`);
}

/**
 * @returns {Promise<Record<string, string[]>>} keys = normalized theater key (e.g. GALAXY)
 */
export async function fetchTheatreGalleryByNames(displayNames) {
  if (!Array.isArray(displayNames) || !displayNames.length) return {};
  try {
    const res = await fetch(THEATRE_GALLERY_BY_NAMES, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ names: displayNames }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data && typeof data === "object" ? data : {};
  } catch {
    return {};
  }
}

/** Normalize API payload: per-theatre list of URLs (supports legacy single-string values). */
export function normalizeTheatreVideoMapFromApi(data) {
  if (!data || typeof data !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    if (Array.isArray(v)) {
      const urls = v.map((x) => String(x).trim()).filter(Boolean);
      if (urls.length) out[k] = urls;
    } else if (v != null && String(v).trim()) {
      out[k] = [String(v).trim()];
    }
  }
  return out;
}

/** @returns {Promise<Record<string, string[]>>} normalized theatre keys → promo video URLs */
export async function fetchTheatreVideosByNames(displayNames) {
  if (!Array.isArray(displayNames) || !displayNames.length) return {};
  try {
    const res = await fetch(THEATRE_GALLERY_VIDEOS_BY_NAMES, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ names: displayNames }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return normalizeTheatreVideoMapFromApi(data);
  } catch {
    return {};
  }
}

/** @returns {Promise<Record<string, { lines?: { icon?: string, text?: string }[] }>>} */
export async function fetchTheatreExperienceByNames(displayNames) {
  if (!Array.isArray(displayNames) || !displayNames.length) return {};
  try {
    const res = await fetch(THEATRE_EXPERIENCE_HIGHLIGHTS_BY_NAMES, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ names: displayNames }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data && typeof data === "object" ? data : {};
  } catch {
    return {};
  }
}

const req = require.context(
  "../images/Theatre_images",
  true,
  /\.(jpe?g|png|webp|gif|avif)$/i
);

function buildFolderMap() {
  const map = Object.create(null);
  req.keys().forEach((key) => {
    const clean = key.replace(/^\.\//, "");
    const parts = clean.split("/");
    const folder =
      parts.length === 1 ? "__ROOT__" : parts[0].toUpperCase();
    if (!map[folder]) map[folder] = [];
    map[folder].push({ key: clean, src: req(key) });
  });
  Object.keys(map).forEach((k) => {
    map[k].sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
  });
  return map;
}

const FOLDER_MAP = buildFolderMap();

export function normalizeTheaterFolderKey(name) {
  if (name == null || String(name).trim() === "") return "";
  return String(name)
    .replace(/\btheatre\b/gi, "")
    .replace(/\btheater\b/gi, "")
    .trim()
    .toUpperCase();
}

/**
 * @returns {string[]} webpack-resolved image URLs (may be empty)
 */
export function getGalleryImagesForTheater(theaterName) {
  const key = normalizeTheaterFolderKey(theaterName);
  if (!key) return [];
  if (FOLDER_MAP[key]?.length) {
    return FOLDER_MAP[key].map((e) => e.src);
  }
  const alt = Object.keys(FOLDER_MAP).find(
    (k) => k !== "__ROOT__" && (key.includes(k) || k.includes(key))
  );
  if (alt && FOLDER_MAP[alt]?.length) {
    return FOLDER_MAP[alt].map((e) => e.src);
  }
  return [];
}

export function getRootPoolImages() {
  const root = FOLDER_MAP.__ROOT__;
  return root?.length ? root.map((e) => e.src) : [];
}

/** Ordered list for hero + thumbnails; CMS `theaterMedia` overrides bundled images. */
export function getTheaterMediaEntry(theaterName, theaterMediaMap) {
  const key = normalizeTheaterFolderKey(theaterName);
  if (!key || !theaterMediaMap || typeof theaterMediaMap !== "object")
    return null;
  if (theaterMediaMap[key]) return theaterMediaMap[key];
  const alt = Object.keys(theaterMediaMap).find(
    (k) => key.includes(k) || k.includes(key)
  );
  return alt ? theaterMediaMap[alt] : null;
}

export function resolveTheaterGallery(theaterName, theaterMediaMap, dynamicUrlsByKey) {
  const entry = getTheaterMediaEntry(theaterName, theaterMediaMap);
  const cmsImages = entry?.images;
  if (Array.isArray(cmsImages) && cmsImages.length > 0) {
    return cmsImages.filter(Boolean);
  }

  const key = normalizeTheaterFolderKey(theaterName);
  const fromApi = key && dynamicUrlsByKey ? dynamicUrlsByKey[key] : null;
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return fromApi.map((u) => absoluteMediaUrl(u)).filter(Boolean);
  }

  const gallery = getGalleryImagesForTheater(theaterName);
  if (gallery.length) return gallery;

  return [];
}

/**
 * Map searchSlots theatres → gallery sections: only theatres returned by the API,
 * images from `src/images/Theatre_images/<folder>/` (and CMS `theaterMedia` overrides).
 * Headings use the API theatre name (`displayName`).
 */
export function buildGallerySectionsFromApiTheaters(
  theatersList,
  theaterMediaMap,
  dynamicUrlsByKey
) {
  const list = Array.isArray(theatersList) ? theatersList : [];
  const sections = [];
  for (const t of list) {
    const apiName =
      t?.name ??
      t?.theatreName ??
      t?.theaterName ??
      t?.theatre ??
      "";
    const displayName = String(apiName).trim();
    if (!displayName) continue;

    const raw = resolveTheaterGallery(
      displayName,
      theaterMediaMap ?? {},
      dynamicUrlsByKey
    );
    const images = raw
      .map((src) => resolveTheaterImageSrc(src))
      .filter(Boolean);
    if (!images.length) continue;

    sections.push({
      id: t?.id ?? normalizeTheaterFolderKey(displayName),
      displayName,
      images,
    });
  }
  return sections;
}
